import { css } from "@emotion/react";
import { FC, PropsWithChildren } from "react";
import {
  MdOutlineDangerous,
  MdOutlineInfo,
  MdOutlineWarning,
} from "react-icons/md";
import { match } from "ts-pattern";

const calloutKind = ["info", "warning", "danger"] as const;
type CalloutKind = (typeof calloutKind)[number];

type Props = {
  kind: CalloutKind;
};

const Color = {
  callout: {
    info: {
      backgroundColor: "#f1f1ef",
      borderColor: "#ccc",
      textColor: "#222",
      iconColor: "#3a87ad",
    },
    warning: {
      backgroundColor: "#fcf8e3",
      borderColor: "#faebcc",
      iconColor: "#8a6d3b",
    },
    danger: {
      backgroundColor: "#f2dede",
      borderColor: "#ebccd1",
      iconColor: "#a94442",
    },
  },
} as const;

export const Callout: FC<PropsWithChildren<Props>> = ({ kind, children }) => {
  const Icon = () =>
    match(kind)
      .with("info", () => <MdOutlineInfo size={"1.5rem"} />)
      .with("warning", () => <MdOutlineWarning size={"1.2rem"} />)
      .with("danger", () => <MdOutlineDangerous size={"1.2rem"} />)
      .exhaustive();

  const flavorStyle = (kind: CalloutKind) =>
    match(kind)
      .with("info", () => infoFlavorStyle)
      .with("warning", () => warningFlavorStyle)
      .with("danger", () => dangerFlavorStyle)
      .exhaustive();

  return (
    <div css={[rootStyle, flavorStyle(kind)]}>
      <div css={iconContainerStyle}>
        <Icon />
      </div>
      <section>{children}</section>
    </div>
  );
};

const rootStyle = css`
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  p {
    line-height: 1.4rem;
    @media screen and (max-width: 500px) {
      font-size: 0.8rem;
      line-height: 1.2rem;
    }
  }
`;

const infoFlavorStyle = css`
  background: ${Color.callout.info.backgroundColor};
  color: ${Color.callout.info.textColor};
  border: 2px solid ${Color.callout.info.borderColor};
`;

const warningFlavorStyle = css`
  background: ${Color.callout.warning.backgroundColor};
  border: 2px solid ${Color.callout.warning.borderColor};
`;

const dangerFlavorStyle = css`
  background: ${Color.callout.danger.backgroundColor};
  border: 2px solid ${Color.callout.danger.borderColor};
`;

const iconContainerStyle = css`
  margin-right: 0.5rem;
`;

export default Callout;
